/// <reference types="@types/googlemaps" />
import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { MatDialogRef } from '@angular/material/dialog';

import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UserGeolocationService } from 'src/app/core/services/user-geolocation.service';
import { APP_STATE_TOKEN } from 'src/app/core/store/app.store.state';
import { IGeoCoordinate, IUserGeolocation } from 'src/app/core/types/user-geolocation.interface';
import { CONSTANTS } from 'src/app/core/utils/constants';

@Component({
  templateUrl: './modal-cambiar-ubicacion.component.html',
  styleUrls: ['./modal-cambiar-ubicacion.component.scss']
})
export class ModalCambiarUbicacionComponent implements OnInit {

  defaultPosition: IGeoCoordinate = { lat: -12.062106, lng: -77.036526 };

  position: IGeoCoordinate = null;

  address: string;

  mapOptions: google.maps.MapOptions;

  infoWindowOptions: google.maps.InfoWindowOptions;

  center: google.maps.LatLngLiteral = this.defaultPosition;

  zoom = 14;

  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;

  @ViewChild(MapMarker, { static: false }) public markerReference: MapMarker;

  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;

  @ViewChild("searchPlace", { static: true }) searchPlace: ElementRef;

  geocoder = new google.maps.Geocoder;

  marker: {
    position: google.maps.LatLngLiteral,
    label: string,
    title: string,
    options: google.maps.MarkerOptions
  } = {
      position: null,
      label: null,
      title: null,
      options: { draggable: true }
    };

  infoWindowMessage: string = null;

  constructor(
    public dialogRef: MatDialogRef<ModalCambiarUbicacionComponent>,
    private ngZone: NgZone,
    private store: Store,
    private userGeolocationService: UserGeolocationService,
    private cd: ChangeDetectorRef
  ) {
    this.setMapOptions();
    this.setInfoWindowOptions();
  }

  ngOnInit() {
    this.geocoder = new google.maps.Geocoder();
  }

  ngAfterViewInit() {
    this.setAutoComplete();
    this.getUserGeoLocation();
    this.cd.detectChanges();
  }

  // ===========================================================
  // #region METHODS
  // ===========================================================

  private setMapOptions = () => {
    this.mapOptions = {
      ...CONSTANTS.DEFAULT_MAP_OPTIONS,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      maxZoom: 15,
      minZoom: 8,
      fullscreenControl: false
    };
  }

  private setInfoWindowOptions = () => {
    this.infoWindowOptions = {
      position: this.defaultPosition
    };
  }

  private getUserGeoLocation = () => {
    const state = this.store.selectSnapshot(APP_STATE_TOKEN);
    const { userGeoLocation } = state.globalConfig;
    if (!userGeoLocation) {
      return;
    }
    this.setPosition(userGeoLocation.position);
    this.setMapCenter(userGeoLocation.position);

    this.setAddress(userGeoLocation.address);
    this.setMarkerPosition(userGeoLocation.position);
    this.setInfoWindowMessage(userGeoLocation.address);
    this.setMapZoom(15);
  }

  private onGeolocationOk = (position: IGeoCoordinate) => {
    this.setPosition(position);
    this.setMapZoom(15);
    this.setInfoWindowMessage('Ubicación encontrada.');
    this.geocodingReverse(position);
    this.setMapCenter(position);
  }

  private onGeoLocationError = (browserHasGeolocation, pos) => {
    const msg = (browserHasGeolocation ?
      'No se pudo encontrar tu ubicación. Por favor busca tu ubicación.' :
      'Tu navegador no soporta geolocalización. Por favor busca tu ubicación.');
    this.setInfoWindowMessage(msg);
  }

  private geocodingReverse = (position: IGeoCoordinate) => {
    this.geocoder.geocode({ 'location': position }, (results, status) => {
      this.ngZone.run(() => {
        if (status === google.maps.GeocoderStatus.OK) {

          if (results.length > 0) {

            this.setMarkerPosition(position);
            const direccion = this.getDireccionFromResults(results);
            this.setAddress(direccion);
            this.setMapZoom(15);
            this.setInfoWindowMessage(direccion);

          } else {
            this.setInfoWindowMessage('No se encontró una dirección válida.');
          }
        } else {
          this.setInfoWindowMessage(`No se encontró una dirección válida. (${status})`);
        }
      });
    });
  };

  private getDireccionFromResults = (results: Array<google.maps.GeocoderResult>) => {
    const filteresResults = results.filter(
      x => x.geometry.location_type == google.maps.GeocoderLocationType.GEOMETRIC_CENTER
    );
    if (filteresResults.length > 0) {
      return filteresResults[0].formatted_address;
    }
    return results[0].formatted_address;
  }

  private setAutoComplete = () => {
    let autocomplete = new google.maps.places.Autocomplete(this.searchPlace.nativeElement);

    autocomplete.addListener("place_changed", () => {

      this.ngZone.run(() => {

        let place: google.maps.places.PlaceResult = autocomplete.getPlace();

        if (place.geometry === undefined || place.geometry === null) {
          return;
        }

        this.position = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };

        this.setAddress(place.formatted_address);

        this.setMapCenter(this.position);

        this.setMarkerPosition(this.position);

        this.setInfoWindowMessage(this.address);
      });
    });
  }

  // ===========================================================
  // #endregion
  // ===========================================================


  // ===========================================================
  // #region HANDLER METHODS
  // ===========================================================

  handleTryGetGeoLocation = () => {
    this.searchPlace.nativeElement.value = '';
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.onGeolocationOk({ lat: position.coords.latitude, lng: position.coords.longitude });
      }, () => {
        this.onGeoLocationError(true, this.map.getCenter());
      });
    } else {
      // Browser doesn't support Geolocation
      this.onGeoLocationError(false, this.map.getCenter());
    }
  }

  handleSubmitGeolocation = () => {
    this.userGeolocationService.setUserGeolocation({
      position: this.position,
      address: this.address
    });
    this.dialogRef.close();
  }

  handleClose = () => {
    this.dialogRef.close();
  }

  handleDragEnd = (e) => {
    this.setPosition({
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    });

    this.geocodingReverse(this.position);
  }

  private setInfoWindowMessage = (msg: string) => {
    this.infoWindowMessage = msg;
    this.infoWindowOptions = {
      ...this.infoWindowOptions,
      position: this.position
    };

    this.infoWindow.open(this.markerReference);
  }

  private setMarkerPosition = (position: IGeoCoordinate) => {
    this.marker = {
      ...this.marker,
      position: position
    };
  }

  private setMapCenter = (center: IGeoCoordinate) => {
    this.center = center;
  }

  private setMapZoom = (zoom: number) => {
    this.zoom = zoom;
  }

  private setPosition = (position: IGeoCoordinate) => {
    this.position = position;
  }

  private setAddress = (address: string) => {
    this.address = address;
  }
}
